import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ReportsService } from '../../services/reports.service';
import { PrintService } from '../../services/print.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { CustomerService } from '../../services/customer.service';

/**
 * Promo / Intro Performance — units, list value and sale value for every deal
 * and intro offer, measured on invoiced movement so credits net off.
 *
 *   Promos        one row per deal header SKU. Expand a row for the invoices
 *                 behind it, or switch to the contents of the deal.
 *   Customers     one row per account taking the deals.
 *
 * There is a third view's worth of data behind /apir/reports/promo_intro/setup —
 * live vs expired offers, live offers nobody has bought in a year, mix-and-match
 * rules matching no products. The endpoint and getPromoIntroSetup() are kept, but
 * the tab is deliberately not rendered; re-enable by restoring the markup.
 *
 * A deal is a header SKU (stockmaster.mbflag = 'C') plus the component lines it
 * entitles. Header and components only make sense summed together — the header
 * is non-stock and carries the price, the components carry the units — so every
 * amount here is the whole group, never the header line alone.
 *
 * Date range, brand, rep, branch and customer are server-side filters. Brand and
 * rep are multi-select, so they apply on Apply rather than on every option click —
 * a re-fetch per tick would fire a query the user has not finished describing.
 *
 * Two client-side searches sit on top of what is already loaded, so both are
 * instant: the toolbar one over the grid, and one inside an expanded deal over its
 * invoices or contents. When either is active the tiles and the totals row
 * recompute from the visible rows rather than reporting a set that is no longer on
 * screen.
 */
@Component({
	selector: 'app-promo-intro',
	templateUrl: './promo-intro.component.html',
	styleUrls: ['./promo-intro.component.scss']
})
export class PromoIntroComponent implements OnInit, OnDestroy {
	@ViewChild('printTable') printTableRef: ElementRef;

	private destroy$ = new Subject<void>();

	filterForm: UntypedFormGroup;
	searcher = new UntypedFormControl('');
	drillSearcher = new UntypedFormControl('');

	// Customer filter. Too many accounts for a picker, so it is a typeahead against
	// the same customer search the rest of the app uses.
	customerSearch = new UntypedFormControl('');
	customerResults: any[] = [];
	customerSearching = false;
	customer: any = null;
	private customerSub: any = null;

	view: 'promos' | 'customers' = 'promos';
	loading = false;
	exporting = false;
	errorMessage = '';

	sort = '';
	dir: 'asc' | 'desc' = 'desc';

	rows: any[] = [];
	searchresults: any[] = [];
	totals: any = null;
	truncated = false;
	limit = 500;

	salesmen: any[] = [];
	branches: any[] = [];
	brands: any[] = [];

	// Drill-in, rendered as a row underneath the deal it belongs to.
	expandedSku = '';
	selected: any = null;
	componentRows: any[] = [];
	componentTotals: any = null;
	componentsLoading = false;
	orderRows: any[] = [];
	orderResults: any[] = [];
	ordersLoading = false;
	componentResults: any[] = [];
	drill: 'components' | 'orders' = 'orders';

	constructor(
		private fb: UntypedFormBuilder,
		private reportsService: ReportsService,
		private printService: PrintService,
		private globalSearchService: GlobalSearchService,
		private customerService: CustomerService,
	) {}

	ngOnInit(): void {
		const today = new Date();
		const first = new Date(today.getFullYear(), today.getMonth(), 1);

		this.filterForm = this.fb.group({
			from: [this.isoDate(first)],
			to: [this.isoDate(today)],
			brand: [[]],
			salesman: [[]],
			branch: [''],
		});

		this.searcher.valueChanges.pipe(
			debounceTime(200),
			distinctUntilChanged(),
			takeUntil(this.destroy$)
		).subscribe(() => this.filterSearch());

		this.drillSearcher.valueChanges.pipe(
			debounceTime(200),
			distinctUntilChanged(),
			takeUntil(this.destroy$)
		).subscribe(() => this.filterDrill());

		this.customerSearch.valueChanges.pipe(
			debounceTime(250),
			distinctUntilChanged(),
			takeUntil(this.destroy$)
		).subscribe((v: any) => this.lookupCustomers(v));

		this.reportsService.getPromoIntroFilters().subscribe((r: any) => {
			if (!r?.success) { return; }
			this.salesmen = r.salesmen || [];
			this.branches = r.branches || [];
			this.brands = r.brands || [];
		});

		this.load();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	load(): void {
		this.closeDrill();

		this.loading = true;
		this.errorMessage = '';

		const call = this.view === 'promos'
			? this.reportsService.getPromoIntroPromos(this.params())
			: this.reportsService.getPromoIntroCustomers(this.params());

		call.subscribe((r: any) => {
			this.loading = false;
			if (!r?.success) {
				this.rows = [];
				this.searchresults = [];
				this.totals = null;
				this.errorMessage = r?.message || 'Could not load promo sales for this range.';
				return;
			}
			this.rows = r.rows || [];
			this.totals = r.totals || null;
			this.truncated = !!r.truncated;
			this.limit = r.limit || this.limit;
			this.filterSearch();
		});
	}

	/** Client-side overlay on the loaded rows — instant, no round trip. */
	filterSearch(): void {
		const term = (this.searcher.value || '').toString().trim();
		if (!term) {
			this.searchresults = this.rows;
			return;
		}
		const keys = this.view === 'promos'
			? 'promo_sku,promo_name,brand,brand_name'
			: 'debtorno,customer,salesman_name,branchcode';
		this.searchresults = this.globalSearchService.filterItem(this.rows, term, keys);
	}

	get searching(): boolean {
		return !!(this.searcher.value || '').toString().trim();
	}

	/**
	 * Customer typeahead. The control holds free text until an option is picked;
	 * `customer` is what actually filters, so clearing the box clears the filter
	 * rather than leaving a stale account pinned to the report.
	 */
	lookupCustomers(term: any): void {
		const text = (typeof term === 'string' ? term : '').trim();

		if (this.customer && text !== this.customerLabel(this.customer)) {
			this.customer = null;
		}
		if (this.customerSub) { this.customerSub.unsubscribe(); }
		if (text.length < 2) {
			this.customerResults = [];
			this.customerSearching = false;
			return;
		}

		this.customerSearching = true;
		this.customerSub = this.customerService.getCustomerSearch({
			source: 'promointro',
			keywords: text,
		}).subscribe((r: any) => {
			this.customerSearching = false;
			this.customerResults = Array.isArray(r) ? r : [];
		}, () => { this.customerSearching = false; });
	}

	customerLabel(c: any): string {
		return c ? c.debtorno + ' — ' + (c.name || '') : '';
	}

	pickCustomer(c: any): void {
		this.customer = c;
		this.customerSearch.setValue(this.customerLabel(c), { emitEvent: false });
		this.customerResults = [];
		this.load();
	}

	clearCustomer(): void {
		this.customer = null;
		this.customerResults = [];
		this.customerSearch.setValue('', { emitEvent: false });
		this.load();
	}

	/** Client-side overlay on the expanded deal's invoices and contents. */
	filterDrill(): void {
		const term = (this.drillSearcher.value || '').toString().trim();
		if (!term) {
			this.orderResults = this.orderRows;
			this.componentResults = this.componentRows;
			return;
		}
		this.orderResults = this.globalSearchService.filterItem(this.orderRows, term,
			'orderno,transno,debtorno,customer,salesman_name,loccode');
		this.componentResults = this.globalSearchService.filterItem(this.componentRows, term,
			'stockid,description');
	}

	get drillSearching(): boolean {
		return !!(this.drillSearcher.value || '').toString().trim();
	}

	/**
	 * Contents totals follow the drill search for the same reason the grid totals
	 * follow the toolbar search: a total that does not add up the visible rows
	 * reads as an arithmetic error.
	 */
	get displayComponentTotals(): any {
		if (!this.drillSearching) { return this.componentTotals; }
		if (!this.componentResults.length) { return null; }

		const sum = (k: string) => this.componentResults.reduce((a: number, r: any) => a + Number(r[k] || 0), 0);
		return {
			units: sum('units'),
			list_value: sum('list_value'),
			net_sales: sum('net_sales'),
		};
	}

	/**
	 * Tiles and the totals row follow what is on screen. Reporting the server's
	 * range totals next to a filtered grid reads as an arithmetic error.
	 */
	get displayTotals(): any {
		if (!this.searching) { return this.totals; }
		if (!this.searchresults.length) { return null; }

		const sum = (k: string) => this.searchresults.reduce((a: number, r: any) => a + Number(r[k] || 0), 0);
		return {
			rows: this.searchresults.length,
			deals: sum('deals'),
			units: sum('component_units'),
			list_value: sum('list_value'),
			net_sales: sum('net_sales'),
		};
	}

	setView(v: 'promos' | 'customers'): void {
		if (this.view === v) { return; }
		this.view = v;
		// Sort and search keys differ per view; carrying either over would filter
		// on a column that no longer exists.
		this.sort = '';
		this.dir = 'desc';
		this.searcher.setValue('', { emitEvent: false });
		this.load();
	}

	/**
	 * Expand one deal in place. The detail belongs directly under the row it
	 * explains — parked at the bottom of the page it loses the link to what was
	 * clicked, especially once the grid is longer than the viewport.
	 *
	 * Opens on the invoices: the usual next question is which paperwork the
	 * number came off, not what is inside the kit.
	 */
	openPromo(row: any): void {
		if (this.expandedSku === row.promo_sku) {
			this.closeDrill();
			return;
		}

		this.expandedSku = row.promo_sku;
		this.selected = row;
		this.drill = 'orders';
		this.componentRows = [];
		this.componentResults = [];
		this.componentTotals = null;
		this.orderRows = [];
		this.orderResults = [];
		this.drillSearcher.setValue('', { emitEvent: false });

		this.loadOrders();
	}

	isExpanded(row: any): boolean {
		return this.expandedSku === row.promo_sku;
	}

	showComponents(): void {
		this.drill = 'components';
		if (this.componentRows.length || !this.selected) { return; }

		this.componentsLoading = true;
		this.reportsService.getPromoIntroComponents(this.selected.promo_sku, this.params()).subscribe((r: any) => {
			this.componentsLoading = false;
			if (!r?.success) { return; }
			this.componentRows = r.rows || [];
			this.componentTotals = r.totals || null;
			this.filterDrill();
		});
	}

	showOrders(): void {
		this.drill = 'orders';
		this.loadOrders();
	}

	private loadOrders(): void {
		if (this.orderRows.length || !this.selected) { return; }

		this.ordersLoading = true;
		this.reportsService.getPromoIntroOrders(this.selected.promo_sku, this.params()).subscribe((r: any) => {
			this.ordersLoading = false;
			this.orderRows = r?.success ? (r.rows || []) : [];
			this.filterDrill();
		});
	}

	closeDrill(): void {
		this.expandedSku = '';
		this.selected = null;
		this.componentRows = [];
		this.componentResults = [];
		this.componentTotals = null;
		this.orderRows = [];
		this.orderResults = [];
		this.drillSearcher.setValue('', { emitEvent: false });
	}

	/**
	 * Deal type for the grid column. `promo_type` is the cart's rule number, and
	 * only means anything on a SKU that is actually set up as a promo — an
	 * unconfigured header SKU sold as a deal has no rule behind it.
	 */
	dealType(row: any): string {
		if (!row || Number(row.configured) !== 1) { return 'Not set up'; }
		switch (Number(row.promo_type)) {
		case 2: return 'Discounted total';
		case 3: return 'Mix & match';
		case 4: return 'Child price + extras';
		default: return row.promo_type === null || row.promo_type === undefined || row.promo_type === ''
			? '' : 'Type ' + row.promo_type;
		}
	}

	sortBy(key: string): void {
		if (this.sort === key) {
			this.dir = this.dir === 'desc' ? 'asc' : 'desc';
		} else {
			this.sort = key;
			this.dir = 'desc';
		}
		this.load();
	}

	sortIcon(key: string): string {
		if (this.sort !== key) { return ''; }
		return this.dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	// ---- export -----------------------------------------------------------

	private get exportTitle(): string {
		return this.view === 'customers' ? 'Promo & Intro — by Customer' : 'Promo & Intro Performance';
	}

	private get exportSubtitle(): string {
		const f = this.filterForm.value;
		return f.from + ' to ' + f.to + ' · created ' + new Date().toLocaleString();
	}

	private get exportFilename(): string {
		return 'promo-intro-' + this.view + '-' + this.isoDate(new Date());
	}

	/**
	 * Flatten the grid into something a converter can render.
	 *
	 * Order matters. Icons go first: the exporters read text nodes, so a ligature
	 * would otherwise land in a cell as the literal word "arrow_downward" or
	 * "expand_more". Buttons go second, and are *unwrapped* rather than removed —
	 * every column header and every deal name is a real <button> for keyboard
	 * access, so deleting them would empty the header row, while leaving them
	 * makes wkhtmltopdf paint grey button chrome around half the page.
	 */
	private buildExportHtml(): string {
		const clone = this.printTableRef.nativeElement.cloneNode(true) as HTMLElement;

		clone.querySelectorAll('.donotprint, .noprint').forEach((n: Element) => n.remove());
		clone.querySelectorAll('mat-icon, .mat-icon, i.material-icons').forEach((n: Element) => n.remove());

		clone.querySelectorAll('button').forEach((b: Element) => {
			const text = (b.textContent || '').replace(/\s+/g, ' ').trim();
			b.replaceWith(clone.ownerDocument.createTextNode(text));
		});

		return clone.innerHTML;
	}

	exportXls(): void {
		if (!this.printTableRef) { return; }
		this.exporting = true;
		const data = {
			content: this.globalSearchService.base64encode(this.buildExportHtml()),
			filename: this.exportFilename,
			title: this.exportTitle,
			subtitle: this.exportSubtitle,
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.exporting = false;
		}, () => { this.exporting = false; });
	}

	exportPdf(): void {
		if (!this.printTableRef) { return; }
		this.exporting = true;
		const data = {
			content: this.globalSearchService.base64encode(this.buildExportHtml()),
			filename: this.exportFilename,
			title: this.exportTitle,
			subtitle: this.exportSubtitle,
			// Too many columns to fit portrait.
			landscape: true,
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.exporting = false;
		}, () => { this.exporting = false; });
	}

	get hasExportableRows(): boolean {
		return this.searchresults.length > 0;
	}

	/** Local calendar date. toISOString() would shift the 1st back a day west of UTC. */
	private isoDate(d: Date): string {
		const m = ('0' + (d.getMonth() + 1)).slice(-2);
		const day = ('0' + d.getDate()).slice(-2);
		return d.getFullYear() + '-' + m + '-' + day;
	}

	/** Multi-selects travel as comma lists; the server whitelists them into an IN (). */
	private params(): any {
		const f = this.filterForm.value;
		return {
			from: f.from,
			to: f.to,
			brand: (f.brand || []).join(','),
			salesman: (f.salesman || []).join(','),
			branch: f.branch || '',
			debtorno: this.customer?.debtorno || '',
			sort: this.sort,
			dir: this.dir,
		};
	}
}
